import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../types/store";
import { selectLoggedInUserEmailOrUsername } from "./loginDetailsSelectors";
import {
  selectUserRoleNameFromPermissions,
  selectUserRoleShortcodeFromPermissions,
} from "./permissionSelectors";

export function selectAuthApiKey(state: RootState): string | undefined {
  const k = state.auth?.user?.key;
  if (k === undefined || k === null) return undefined;
  const s = String(k).trim();
  return s !== "" ? s : undefined;
}

export function selectAuthUsername(state: RootState): string {
  const u = state.auth?.user?.username;
  if (u === undefined || u === null) return "";
  const s = String(u).trim();
  return s !== "" ? s : "";
}

export function isSameUserId(a: unknown, b: unknown): boolean {
  const left = a === undefined || a === null ? "" : String(a).trim();
  const right = b === undefined || b === null ? "" : String(b).trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  return Number.isFinite(leftNum) && Number.isFinite(rightNum) && leftNum === rightNum;
}

export function selectCurrentUserId(state: RootState): string | null {
  const candidates = [
    state.auth?.userId,
    state.loginDetails?.logedInUsersDetails?.user_id,
    state.featureAccess?.userDetails?.user_id,
    state.myProfile?.userDetails?.user_id,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const normalized = String(candidate).trim();
    if (normalized !== "") return normalized;
  }
  return null;
}

export const selectViewerRoleContext = createSelector(
  [
    (state: RootState) => state.role?.role ?? state.loginDetails?.logedInRole ?? null,
    selectUserRoleShortcodeFromPermissions,
    selectUserRoleNameFromPermissions,
  ],
  (role, shortcode, name) => {
    const normalizedRole = role !== null && role !== undefined ? String(role).trim() : "";
    const roleLower = normalizedRole.toLowerCase();
    return {
      role: normalizedRole || null,
      shortcode: shortcode ?? null,
      name: name ?? null,
      isClient: roleLower === "client",
    };
  }
);

export function selectChatSenderIdentity(state: RootState): string | null {
  const fromLogin = selectLoggedInUserEmailOrUsername(state);
  if (fromLogin) return fromLogin;
  const u = state.auth?.user?.username;
  if (u === undefined || u === null) return null;
  const s = String(u).trim();
  return s !== "" ? s : null;
}

export function selectUserRole(state: RootState): string | null {
  const r = state.role?.role ?? state.loginDetails?.logedInRole;
  if (r === undefined || r === null) return null;
  const s = String(r).trim();
  return s !== "" ? s : null;
}
